import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetSalaryStructuresQuery,
  useCreateSalaryStructureMutation,
  useUpdateSalaryStructureMutation,
  useDeleteSalaryStructureMutation,
  useCalculateSalaryBreakdownMutation
} from '../../services/salaryStructureApi';
import { useGetEmployeesQuery } from '../../services/employeeApi';
import { addToast } from '../../store/slices/uiSlice';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalculatorIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  CogIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

const SalaryStructure = () => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [calculationData, setCalculationData] = useState({
    grossSalary: '',
    structureId: '',
    employeeId: '',
    customAllowances: []
  });
  const [calculationResult, setCalculationResult] = useState(null);

  const { data: structures, isLoading, error, refetch } = useGetSalaryStructuresQuery({ isActive: true });
  const { data: employeesData } = useGetEmployeesQuery({ limit: 100 }); // Get employees for selection

  // Debug logging
  useEffect(() => {
    console.log('Salary Structures Data:', structures);
    console.log('Loading:', isLoading);
    console.log('Error:', error);
  }, [structures, isLoading, error]);

  const [createStructure] = useCreateSalaryStructureMutation();
  const [updateStructure] = useUpdateSalaryStructureMutation();
  const [deleteStructure] = useDeleteSalaryStructureMutation();
  const [calculateBreakdown] = useCalculateSalaryBreakdownMutation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    allowances: [
      {
        name: 'Basic',
        calculationType: 'percentage_of_gross',
        percentage: 40,
        isTaxable: true,
        isPfApplicable: true,
        isEsiApplicable: true,
        order: 1
      },
        {
          name: 'HRA',
          calculationType: 'percentage_of_basic',
          percentage: 50,
          isTaxable: false, // HRA is tax-exempt under Section 10(13A)
          isPfApplicable: true, // PF applies to HRA
          isEsiApplicable: true,
          order: 2
        },
        {
          name: 'Conveyance',
          calculationType: 'fixed',
          fixedAmount: 1600,
          isTaxable: false,
          isPfApplicable: false,
          isEsiApplicable: true, // Conveyance is ESI applicable
          order: 3
        },
        {
          name: 'Special Allowance',
          calculationType: 'balance',
          isTaxable: true,
          isPfApplicable: false, // Special allowance is not PF applicable
          isEsiApplicable: false, // Special allowance is not ESI applicable
          order: 4
        }
    ],
    statutoryDeductions: {
      pf: {
        employeeContribution: 12,
        employerContribution: 12,
        isRestricted: false,
        maxBasicLimit: 15000
      },
      esi: {
        employeeContribution: 0.75,
        employerContribution: 3.25,
        maxGrossLimit: 21000
      },
      professionalTax: {
        isEnabled: true
      },
      tds: {
        isEnabled: true,
        taxRegime: 'new'
      }
    },
    isDefault: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStructure) {
        await updateStructure({ id: editingStructure._id, ...formData }).unwrap();
        dispatch(addToast({
          type: 'success',
          message: 'Salary structure updated successfully'
        }));
      } else {
        await createStructure(formData).unwrap();
        dispatch(addToast({
          type: 'success',
          message: 'Salary structure created successfully'
        }));
      }
      setIsModalOpen(false);
      setEditingStructure(null);
      resetForm();
      refetch();
    } catch (error) {
      dispatch(addToast({
        type: 'error',
        message: error.data?.message || 'Failed to save salary structure'
      }));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this salary structure?')) {
      try {
        await deleteStructure(id).unwrap();
        dispatch(addToast({
          type: 'success',
          message: 'Salary structure deleted successfully'
        }));
        refetch();
      } catch (error) {
        dispatch(addToast({
          type: 'error',
          message: error.data?.message || 'Failed to delete salary structure'
        }));
      }
    }
  };

  const handleCalculate = async () => {
    console.log('handleCalculate called with data:', calculationData);

    if (!calculationData.grossSalary) {
      dispatch(addToast({
        type: 'error',
        message: 'Please enter gross salary'
      }));
      return;
    }

    // If no structure is selected, use the default structure
    let calcData = { ...calculationData };
    if (!calcData.structureId && structures?.data?.structures?.length > 0) {
      const defaultStructure = structures.data.structures.find(s => s.isDefault) || structures.data.structures[0];
      calcData.structureId = defaultStructure._id;
      console.log('Using default structure:', defaultStructure.name);
    }

    try {
      console.log('Making API call with:', calcData);
      const result = await calculateBreakdown(calcData).unwrap();
      console.log('API result:', result);
      setCalculationResult(result.data);
      dispatch(addToast({
        type: 'success',
        message: 'Salary breakdown calculated successfully'
      }));
    } catch (error) {
      console.error('Calculation error:', error);
      setCalculationResult(null);
      dispatch(addToast({
        type: 'error',
        message: error.data?.message || 'Failed to calculate salary breakdown'
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      allowances: [
        {
          name: 'Basic',
          calculationType: 'percentage_of_gross',
          percentage: 40,
          isTaxable: true,
          isPfApplicable: true,
          isEsiApplicable: true,
          order: 1
        },
        {
          name: 'HRA',
          calculationType: 'percentage_of_basic',
          percentage: 50,
          isTaxable: false, // HRA is tax-exempt under Section 10(13A)
          isPfApplicable: true, // PF applies to HRA
          isEsiApplicable: true,
          order: 2
        },
        {
          name: 'Conveyance',
          calculationType: 'fixed',
          fixedAmount: 1600,
          isTaxable: false,
          isPfApplicable: false,
          isEsiApplicable: true, // Conveyance is ESI applicable
          order: 3
        },
        {
          name: 'Special Allowance',
          calculationType: 'balance',
          isTaxable: true,
          isPfApplicable: false, // Special allowance is not PF applicable
          isEsiApplicable: false, // Special allowance is not ESI applicable
          order: 4
        }
      ],
      statutoryDeductions: {
        pf: {
          employeeContribution: 12,
          employerContribution: 12,
          isRestricted: false,
          maxBasicLimit: 15000
        },
        esi: {
          employeeContribution: 0.75,
          employerContribution: 3.25,
          maxGrossLimit: 21000
        },
        professionalTax: {
          isEnabled: true
        },
        tds: {
          isEnabled: true,
          taxRegime: 'new'
        }
      },
      isDefault: false
    });
  };

  const addAllowance = () => {
    setFormData(prev => ({
      ...prev,
      allowances: [...prev.allowances, {
        name: '',
        calculationType: 'fixed',
        percentage: 0,
        fixedAmount: 0,
        isTaxable: true,
        isPfApplicable: true,
        isEsiApplicable: true,
        order: prev.allowances.length + 1
      }]
    }));
  };

  const updateAllowance = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      allowances: prev.allowances.map((allowance, i) =>
        i === index ? { ...allowance, [field]: value } : allowance
      )
    }));
  };

  const removeAllowance = (index) => {
    setFormData(prev => ({
      ...prev,
      allowances: prev.allowances.filter((_, i) => i !== index)
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading salary structures...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-800">Error Loading Data</h3>
                <p className="text-red-600 mt-1">
                  {error?.data?.message || 'Failed to load salary structures. Please try again.'}
                </p>
                <button
                  onClick={refetch}
                  className="mt-3 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center">
                  <CurrencyDollarIcon className="h-8 w-8 mr-3" />
                  Salary Structures
                </h1>
                <p className="mt-2 text-indigo-100 text-lg">
                  Manage company salary structures and allowances
                </p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => setCalculatorOpen(true)}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-6 py-3 rounded-xl hover:bg-white/20 transition-all duration-200 flex items-center shadow-lg"
                >
                  <CalculatorIcon className="h-5 w-5 mr-2" />
                  Calculator
                </button>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-white text-indigo-600 px-6 py-3 rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center shadow-lg font-semibold"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Structure
                </button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="px-6 py-6 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Structures</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {structures?.data?.structures?.length || 0}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <CheckCircleSolidIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Structures</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {structures?.data?.structures?.filter(s => s.isActive)?.length || 0}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <CogIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Default Structure</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {structures?.data?.structures?.filter(s => s.isDefault)?.length || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Salary Structures Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {structures?.data?.structures && structures.data.structures.length > 0 ? (
            structures.data.structures.map((structure) => (
              <div
                key={structure._id}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-indigo-100 p-2 rounded-lg">
                        <DocumentTextIcon className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                          {structure.name}
                        </h3>
                        {structure.isDefault && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircleSolidIcon className="h-3 w-3 mr-1" />
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setEditingStructure(structure);
                          setFormData({
                            name: structure.name,
                            description: structure.description,
                            allowances: structure.allowances || [],
                            statutoryDeductions: structure.statutoryDeductions || {},
                            isDefault: structure.isDefault
                          });
                          setIsModalOpen(true);
                        }}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Edit Structure"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(structure._id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Structure"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="px-6 py-4">
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {structure.description || 'No description provided'}
                  </p>

                  {/* Allowance Count */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <div className="bg-blue-100 p-1.5 rounded-md">
                        <CurrencyDollarIcon className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {structure.allowances?.length || 0} Allowances
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Created {new Date(structure.createdAt).toLocaleDateString()}
                    </div>
                  </div>

                  {/* Allowance Preview */}
                  {structure.allowances && structure.allowances.length > 0 && (
                    <div className="space-y-2 mb-4">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Top Allowances</p>
                      <div className="space-y-1">
                        {structure.allowances.slice(0, 3).map((allowance, index) => (
                          <div key={index} className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">{allowance.name}</span>
                            <div className="flex space-x-1">
                              {allowance.isTaxable && (
                                <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">T</span>
                              )}
                              {allowance.isPfApplicable && (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">PF</span>
                              )}
                              {allowance.isEsiApplicable && (
                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">ESI</span>
                              )}
                            </div>
                          </div>
                        ))}
                        {structure.allowances.length > 3 && (
                          <p className="text-xs text-gray-500">
                            +{structure.allowances.length - 3} more allowances
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setCalculationData(prev => ({ ...prev, structureId: structure._id }));
                        setCalculatorOpen(true);
                      }}
                      className="flex-1 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium flex items-center justify-center"
                    >
                      <CalculatorIcon className="h-4 w-4 mr-2" />
                      Calculate
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
                <div className="bg-gray-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Salary Structures</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Get started by creating your first salary structure to define allowances and statutory deductions for your employees.
                </p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-xl hover:bg-indigo-700 transition-colors font-semibold inline-flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create Your First Structure
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Calculator Modal */}
      {calculatorOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 backdrop-blur-sm"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                <div className="flex items-center">
                  <CalculatorIcon className="h-6 w-6 text-white mr-3" />
                  <h3 className="text-lg leading-6 font-semibold text-white">
                    Salary Calculator
                  </h3>
                </div>
              </div>
              <div className="bg-white px-6 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Gross Salary (₹)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">₹</span>
                      </div>
                      <input
                        type="number"
                        value={calculationData.grossSalary}
                        onChange={(e) => setCalculationData(prev => ({
                          ...prev,
                          grossSalary: e.target.value
                        }))}
                        className="block w-full pl-8 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-lg font-semibold shadow-sm"
                        placeholder="Enter gross salary"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Employee (Optional - for TDS Declaration Check)
                    </label>
                    <select
                      value={calculationData.employeeId || ''}
                      onChange={(e) => setCalculationData(prev => ({
                        ...prev,
                        employeeId: e.target.value
                      }))}
                      className="block w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                    >
                      <option value="">No employee selected</option>
                      {employeesData?.data?.employees?.map((employee) => (
                        <option key={employee._id} value={employee._id}>
                          {employee.firstName} {employee.lastName} - {employee.employeeId}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Salary Structure
                    </label>
                    <select
                      value={calculationData.structureId}
                      onChange={(e) => setCalculationData(prev => ({
                        ...prev,
                        structureId: e.target.value
                      }))}
                      className="block w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                    >
                      <option value="">Use Default Structure</option>
                      {structures?.data?.structures?.map((structure) => (
                        <option key={structure._id} value={structure._id}>
                          {structure.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse gap-3">
                <button
                  onClick={handleCalculate}
                  className="w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-base font-semibold text-white hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                >
                  <CalculatorIcon className="h-5 w-5 mr-2" />
                  Calculate Breakdown
                </button>
                <button
                  onClick={() => setCalculatorOpen(false)}
                  className="w-full inline-flex justify-center rounded-xl border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calculation Result Modal */}
      {calculationResult && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 backdrop-blur-sm"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CalculatorIcon className="h-6 w-6 text-white mr-3" />
                    <h3 className="text-lg leading-6 font-semibold text-white">
                      Salary Breakdown Result
                    </h3>
                  </div>
                  <button
                    onClick={() => setCalculationResult(null)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="bg-white px-6 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="space-y-6">
                  {/* Main Salary Figures */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
                      <div className="text-center">
                        <div className="text-sm font-medium text-gray-600 mb-2">Gross Salary</div>
                        <div className="text-3xl font-bold text-gray-900">₹{parseFloat(calculationResult.breakdown.gross).toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                      <div className="text-center">
                        <div className="text-sm font-medium text-green-700 mb-2">Net Salary</div>
                        <div className="text-3xl font-bold text-green-600">₹{parseFloat(calculationResult.breakdown.netSalary).toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                      <div className="text-center">
                        <div className="text-sm font-medium text-blue-700 mb-2">Cost to Company</div>
                        <div className="text-3xl font-bold text-blue-600">₹{parseFloat(calculationResult.breakdown.ctc).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Breakdown */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Earnings */}
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-3">
                        <h4 className="text-white font-semibold flex items-center">
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Earnings
                        </h4>
                      </div>
                      <div className="p-4 space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-700 font-medium">Basic Salary</span>
                          <span className="text-gray-900 font-bold">₹{parseFloat(calculationResult.breakdown.basic).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-700 font-medium">HRA</span>
                          <span className="text-gray-900 font-bold">₹{parseFloat(calculationResult.breakdown.hra).toLocaleString()}</span>
                        </div>
                        {calculationResult.breakdown.allowances && calculationResult.breakdown.allowances.map((allowance, index) => (
                          <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-700 font-medium">{allowance.name}</span>
                              <div className="flex space-x-1">
                                {allowance.isTaxable && (
                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium">Taxable</span>
                                )}
                                {allowance.isPfApplicable && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">PF</span>
                                )}
                                {allowance.isEsiApplicable && (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">ESI</span>
                                )}
                              </div>
                            </div>
                            <span className="text-gray-900 font-bold">₹{parseFloat(allowance.amount).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Deductions */}
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-red-500 to-pink-500 px-4 py-3">
                        <h4 className="text-white font-semibold flex items-center">
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          Deductions
                        </h4>
                      </div>
                      <div className="p-4 space-y-3">
                        {calculationResult.breakdown.deductions?.pf && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-700 font-medium">PF (Employee)</span>
                            <span className="text-red-600 font-bold">₹{parseFloat(calculationResult.breakdown.deductions.pf.employee).toLocaleString()}</span>
                          </div>
                        )}
                        {calculationResult.breakdown.deductions?.esi && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-700 font-medium">ESI (Employee)</span>
                            <span className="text-red-600 font-bold">₹{parseFloat(calculationResult.breakdown.deductions.esi.employee).toLocaleString()}</span>
                          </div>
                        )}
                        {calculationResult.breakdown.deductions?.professionalTax && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-700 font-medium">Professional Tax</span>
                            <span className="text-red-600 font-bold">₹{parseFloat(calculationResult.breakdown.deductions.professionalTax).toLocaleString()}</span>
                          </div>
                        )}
                        {calculationResult.breakdown.deductions?.tds && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-700 font-medium">TDS</span>
                            <span className="text-red-600 font-bold">₹{parseFloat(calculationResult.breakdown.deductions.tds).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* TDS Declaration Status */}
                  {calculationResult.tdsDeclaration && (
                    <div className="mt-6">
                      <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold text-gray-900">TDS Declaration Status</h4>
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                            calculationResult.tdsDeclaration.isValid
                              ? 'bg-green-100 text-green-800'
                              : calculationResult.tdsDeclaration.status === 'expired'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {calculationResult.tdsDeclaration.isValid
                              ? 'Valid'
                              : calculationResult.tdsDeclaration.status === 'expired'
                              ? 'Expired'
                              : calculationResult.tdsDeclaration.status}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Declaration Type</p>
                            <p className="font-medium text-gray-900 capitalize">
                              {calculationResult.tdsDeclaration.declarationType.replace('_', ' ')}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Valid Period</p>
                            <p className="font-medium text-gray-900">
                              {new Date(calculationResult.tdsDeclaration.validFrom).toLocaleDateString()} - {new Date(calculationResult.tdsDeclaration.validTo).toLocaleDateString()}
                            </p>
                          </div>
                          {calculationResult.tdsDeclaration.daysUntilExpiry && (
                            <div>
                              <p className="text-sm text-gray-600">Days Until Expiry</p>
                              <p className={`font-medium ${
                                calculationResult.tdsDeclaration.daysUntilExpiry <= 30
                                  ? 'text-red-600'
                                  : calculationResult.tdsDeclaration.daysUntilExpiry <= 90
                                  ? 'text-yellow-600'
                                  : 'text-green-600'
                              }`}>
                                {calculationResult.tdsDeclaration.daysUntilExpiry} days
                              </p>
                            </div>
                          )}
                          <div>
                            <p className="text-sm text-gray-600">TDS Exemption</p>
                            <p className={`font-medium ${
                              calculationResult.tdsDeclaration.isValid ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {calculationResult.tdsDeclaration.isValid ? 'Applied' : 'Not Applied'}
                            </p>
                          </div>
                        </div>

                        {!calculationResult.tdsDeclaration.isValid && (
                          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex">
                              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                              <div className="ml-3">
                                <p className="text-sm text-yellow-800">
                                  <strong>TDS Declaration {calculationResult.tdsDeclaration.status}</strong>
                                </p>
                                <p className="text-sm text-yellow-700 mt-1">
                                  {calculationResult.tdsDeclaration.status === 'expired'
                                    ? 'This declaration has expired. Employee needs to submit a new declaration for the current financial year.'
                                    : 'This declaration is not approved or has issues. TDS is being calculated normally.'}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse gap-3">
                <button
                  onClick={() => setCalculationResult(null)}
                  className="w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-base font-semibold text-white hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 backdrop-blur-sm"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleSubmit}>
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-6 w-6 text-white mr-3" />
                    <h3 className="text-lg leading-6 font-semibold text-white">
                      {editingStructure ? 'Edit Salary Structure' : 'Create Salary Structure'}
                    </h3>
                  </div>
                </div>
                <div className="bg-white px-6 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="space-y-8">
                    {/* Basic Information */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <DocumentTextIcon className="h-5 w-5 mr-2 text-indigo-600" />
                        Basic Information
                      </h4>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Structure Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm font-medium"
                            placeholder="e.g., Standard Employee Structure"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Description
                          </label>
                          <input
                            type="text"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                            placeholder="Brief description of this structure"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Allowances Section */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                          <CurrencyDollarIcon className="h-5 w-5 mr-2 text-indigo-600" />
                          Allowances ({formData.allowances.length})
                        </h4>
                        <button
                          type="button"
                          onClick={addAllowance}
                          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                          <PlusIcon className="h-4 w-4 mr-2" />
                          Add Allowance
                        </button>
                      </div>

                      <div className="space-y-4">
                        {formData.allowances.map((allowance, index) => (
                          <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Name *
                                </label>
                                <input
                                  type="text"
                                  required
                                  value={allowance.name}
                                  onChange={(e) => updateAllowance(index, 'name', e.target.value)}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Calculation Type *
                                </label>
                                <select
                                  required
                                  value={allowance.calculationType}
                                  onChange={(e) => updateAllowance(index, 'calculationType', e.target.value)}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                >
                                  <option value="fixed">Fixed Amount</option>
                                  <option value="percentage_of_gross">% of Gross</option>
                                  <option value="percentage_of_basic">% of Basic</option>
                                  <option value="balance">Balance</option>
                                </select>
                              </div>
                              {allowance.calculationType === 'percentage_of_gross' || allowance.calculationType === 'percentage_of_basic' ? (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Percentage *
                                  </label>
                                  <input
                                    type="number"
                                    required
                                    value={allowance.percentage}
                                    onChange={(e) => updateAllowance(index, 'percentage', parseFloat(e.target.value))}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                  />
                                </div>
                              ) : allowance.calculationType === 'fixed' ? (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Fixed Amount *
                                  </label>
                                  <input
                                    type="number"
                                    required
                                    value={allowance.fixedAmount}
                                    onChange={(e) => updateAllowance(index, 'fixedAmount', parseFloat(e.target.value))}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                  />
                                </div>
                              ) : null}
                              <div className="flex items-center space-x-4">
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={allowance.isTaxable}
                                    onChange={(e) => updateAllowance(index, 'isTaxable', e.target.checked)}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                  />
                                  <label className="ml-2 text-sm text-gray-700">Taxable</label>
                                </div>
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={allowance.isPfApplicable}
                                    onChange={(e) => updateAllowance(index, 'isPfApplicable', e.target.checked)}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                  />
                                  <label className="ml-2 text-sm text-gray-700">PF</label>
                                </div>
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={allowance.isEsiApplicable}
                                    onChange={(e) => updateAllowance(index, 'isEsiApplicable', e.target.checked)}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                  />
                                  <label className="ml-2 text-sm text-gray-700">ESI</label>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeAllowance(index)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Statutory Deductions */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <CogIcon className="h-5 w-5 mr-2 text-indigo-600" />
                        Statutory Deductions
                      </h4>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">PF Settings</h5>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-4">
                              <div>
                                <label className="block text-xs text-gray-600">Employee (%)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={formData.statutoryDeductions.pf.employeeContribution}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    statutoryDeductions: {
                                      ...prev.statutoryDeductions,
                                      pf: {
                                        ...prev.statutoryDeductions.pf,
                                        employeeContribution: parseFloat(e.target.value)
                                      }
                                    }
                                  }))}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600">Employer (%)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={formData.statutoryDeductions.pf.employerContribution}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    statutoryDeductions: {
                                      ...prev.statutoryDeductions,
                                      pf: {
                                        ...prev.statutoryDeductions.pf,
                                        employerContribution: parseFloat(e.target.value)
                                      }
                                    }
                                  }))}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                            </div>
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formData.statutoryDeductions.pf.isRestricted}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    statutoryDeductions: {
                                      ...prev.statutoryDeductions,
                                      pf: {
                                        ...prev.statutoryDeductions.pf,
                                        isRestricted: e.target.checked
                                      }
                                    }
                                  }))}
                                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 text-xs text-gray-600">Restricted to ₹15,000 Basic</label>
                              </div>
                              {formData.statutoryDeductions.pf.isRestricted && (
                                <div>
                                  <label className="block text-xs text-gray-600">Max Basic Limit</label>
                                  <input
                                    type="number"
                                    value={formData.statutoryDeductions.pf.maxBasicLimit}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      statutoryDeductions: {
                                        ...prev.statutoryDeductions,
                                        pf: {
                                          ...prev.statutoryDeductions.pf,
                                          maxBasicLimit: parseFloat(e.target.value)
                                        }
                                      }
                                    }))}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">ESI Settings</h5>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-4">
                              <div>
                                <label className="block text-xs text-gray-600">Employee (%)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={formData.statutoryDeductions.esi.employeeContribution}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    statutoryDeductions: {
                                      ...prev.statutoryDeductions,
                                      esi: {
                                        ...prev.statutoryDeductions.esi,
                                        employeeContribution: parseFloat(e.target.value)
                                      }
                                    }
                                  }))}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600">Employer (%)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={formData.statutoryDeductions.esi.employerContribution}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    statutoryDeductions: {
                                      ...prev.statutoryDeductions.esi,
                                      employerContribution: parseFloat(e.target.value)
                                    }
                                  }))}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600">Max Gross Limit</label>
                              <input
                                type="number"
                                value={formData.statutoryDeductions.esi.maxGrossLimit}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  statutoryDeductions: {
                                    ...prev.statutoryDeductions,
                                    esi: {
                                      ...prev.statutoryDeductions.esi,
                                      maxGrossLimit: parseFloat(e.target.value)
                                    }
                                  }
                                }))}
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mt-6">
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Professional Tax</h5>
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.statutoryDeductions.professionalTax.isEnabled}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  statutoryDeductions: {
                                    ...prev.statutoryDeductions,
                                    professionalTax: {
                                      ...prev.statutoryDeductions.professionalTax,
                                      isEnabled: e.target.checked
                                    }
                                  }
                                }))}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <label className="ml-2 text-sm text-gray-700">Enable Professional Tax</label>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">TDS Settings</h5>
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.statutoryDeductions.tds.isEnabled}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  statutoryDeductions: {
                                    ...prev.statutoryDeductions,
                                    tds: {
                                      ...prev.statutoryDeductions.tds,
                                      isEnabled: e.target.checked
                                    }
                                  }
                                }))}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <label className="ml-2 text-sm text-gray-700">Enable TDS</label>
                            </div>
                            {formData.statutoryDeductions.tds.isEnabled && (
                              <div>
                                <label className="block text-xs text-gray-600">Tax Regime</label>
                                <select
                                  value={formData.statutoryDeductions.tds.taxRegime}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    statutoryDeductions: {
                                      ...prev.statutoryDeductions,
                                      tds: {
                                        ...prev.statutoryDeductions.tds,
                                        taxRegime: e.target.value
                                      }
                                    }
                                  }))}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                >
                                  <option value="new">New Tax Regime</option>
                                  <option value="old">Old Tax Regime</option>
                                </select>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Default Structure */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isDefault}
                        onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-gray-700">
                        Set as default salary structure
                      </label>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-base font-semibold text-white hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                  >
                    {editingStructure ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingStructure(null);
                      resetForm();
                    }}
                    className="w-full inline-flex justify-center rounded-xl border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryStructure;
